import React, { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import type { ChecklistItem } from '../types';
import * as T from '../ui/inlineTheme';
import { sf } from '../../themeConfig/sisuForgeTokens';

const transition = 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease';

const rowShell: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 10,
  border: `1px solid ${sf.border}`,
  backgroundColor: sf.surfaceElevated,
  padding: '10px 12px',
  boxSizing: 'border-box',
  transition,
  boxShadow: 'none',
};

const gripBtn: React.CSSProperties = {
  padding: 6,
  border: 'none',
  borderRadius: 8,
  background: sf.surface,
  color: sf.textMuted,
  cursor: 'grab',
  display: 'inline-flex',
  touchAction: 'none',
  WebkitAppearance: 'none',
  appearance: 'none',
  flexShrink: 0,
  transition,
};

const removeBtn: React.CSSProperties = {
  padding: 8,
  border: 'none',
  borderRadius: 8,
  background: 'transparent',
  color: sf.textSubtle,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'color 0.15s ease, background-color 0.15s ease',
};

const sectionTitle: React.CSSProperties = {
  ...T.labelSmall,
  marginBottom: 10,
  marginTop: 8,
  color: sf.textMuted,
};

function SortableRow({
  item,
  onToggle,
  onRemove,
  removeDisabled,
}: {
  item: ChecklistItem;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  removeDisabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition: tr, isDragging } = useSortable({
    id: item.id,
  });
  const dndStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: tr,
    opacity: isDragging ? 0.92 : 1,
    zIndex: isDragging ? 2 : undefined,
    boxShadow: isDragging ? '0 8px 24px rgba(0, 0, 0, 0.35)' : 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    fontFamily: T.font,
    fontSize: 16,
    color: sf.text,
    textShadow: 'none',
    minWidth: 0,
  };

  const cbStyle: React.CSSProperties = {
    width: 20,
    height: 20,
    flexShrink: 0,
    accentColor: sf.accent,
    cursor: 'pointer',
  };

  return (
    <li ref={setNodeRef} style={{ ...rowShell, ...dndStyle }}>
      <button
        type="button"
        style={gripBtn}
        data-tb-icon="true"
        aria-label="Reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={18} strokeWidth={2} />
      </button>
      <label style={labelStyle}>
        <input
          type="checkbox"
          checked={item.is_packed}
          onChange={() => onToggle(item.id)}
          style={cbStyle}
        />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
      </label>
      <button
        type="button"
        data-tb-icon="true"
        disabled={removeDisabled}
        style={{
          ...removeBtn,
          opacity: removeDisabled ? 0.35 : 1,
          cursor: removeDisabled ? 'not-allowed' : 'pointer',
        }}
        aria-label={`Remove ${item.label}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(item.id);
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Trash2 size={18} strokeWidth={2} />
      </button>
    </li>
  );
}

function PackedRow({
  item,
  onToggle,
  onRemove,
  removeDisabled,
}: {
  item: ChecklistItem;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  removeDisabled?: boolean;
}) {
  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    fontFamily: T.font,
    fontSize: 16,
    color: sf.textMuted,
    textDecoration: 'line-through',
    textShadow: 'none',
    minWidth: 0,
  };

  const cbStyle: React.CSSProperties = {
    width: 20,
    height: 20,
    flexShrink: 0,
    accentColor: sf.border,
    cursor: 'pointer',
  };

  return (
    <li style={{ ...rowShell, backgroundColor: sf.surface, opacity: 0.85 }}>
      <span style={{ width: 32, flexShrink: 0 }} aria-hidden />
      <label style={labelStyle}>
        <input
          type="checkbox"
          checked
          onChange={() => onToggle(item.id)}
          style={cbStyle}
        />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
      </label>
      <button
        type="button"
        data-tb-icon="true"
        disabled={removeDisabled}
        style={{
          ...removeBtn,
          opacity: removeDisabled ? 0.35 : 1,
          cursor: removeDisabled ? 'not-allowed' : 'pointer',
        }}
        aria-label={`Remove ${item.label}`}
        onClick={(e) => {
          e.preventDefault();
          onRemove(item.id);
        }}
      >
        <Trash2 size={18} strokeWidth={2} />
      </button>
    </li>
  );
}

type Props = {
  items: ChecklistItem[];
  onToggle: (itemId: string) => void;
  onReorder: (orderedUnpackedIds: string[]) => void;
  onRemove: (itemId: string) => void;
  removeDisabled?: boolean;
  /** Section headings above open vs completed rows */
  sectionLabels?: { open: string; done: string };
};

export function SortableChecklist({
  items,
  onToggle,
  onReorder,
  onRemove,
  removeDisabled,
  sectionLabels = { open: 'To pack', done: 'Packed' },
}: Props) {
  const { unpacked, packed } = useMemo(() => {
    const u = items.filter((i) => !i.is_packed);
    const p = items.filter((i) => i.is_packed);
    return { unpacked: u, packed: p };
  }, [items]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = unpacked.findIndex((i) => i.id === active.id);
    const newIndex = unpacked.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(unpacked, oldIndex, newIndex);
    onReorder(next.map((i) => i.id));
  };

  const ids = unpacked.map((i) => i.id);

  const listStyle: React.CSSProperties = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  return (
    <div>
      {unpacked.length > 0 && (
        <>
          <p style={sectionTitle}>{sectionLabels.open}</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <ul style={listStyle}>
                {unpacked.map((item) => (
                  <SortableRow
                    key={item.id}
                    item={item}
                    onToggle={onToggle}
                    onRemove={onRemove}
                    removeDisabled={removeDisabled}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      )}

      {packed.length > 0 && (
        <>
          <p style={{ ...sectionTitle, marginTop: unpacked.length ? 18 : 8 }}>{sectionLabels.done}</p>
          <ul style={listStyle}>
            {packed.map((item) => (
              <PackedRow
                key={item.id}
                item={item}
                onToggle={onToggle}
                onRemove={onRemove}
                removeDisabled={removeDisabled}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
